import PropTypes from 'prop-types';

import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {REQUEST_TASK_GRAPHQL_SHAPE} from 'requests/tasks/RequestTaskConstants';
import {ParentStorableType, STORABLE_SCHEMA_SHAPE} from 'samples/storables/StorableSchemaConstants';
import {SCHEMA_FIELD_SHAPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ArrayElement, Optional} from 'util/Types';
import {STAGE_RUN_FULL_SHAPE} from 'workflows/WorkflowConstants';

import type {TableData} from './TableConstants';
import {TABLE_DEFAULT_COLUMN_WIDTH} from './TableLayoutConstants';

export const COLUMN_FIELD_DEFAULTS = {
  widths: TABLE_DEFAULT_COLUMN_WIDTH,
  columnConstraints: null,
  columnLabels: null,
} satisfies Partial<{
  [T in keyof TableData]: ArrayElement<TableData[T]>;
}>;

// Agent keep in sync: TableColumnType in column_enums.py
export enum TableColumnType {
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
  TAG_VALUE = 'TAG_VALUE',

  // Registration table columns
  BATCH = 'BATCH',
  // WARNING: The following 6 types are no longer in use for new tables but should be retained until existing
  // tables have been migrated to the new unified BASES type.
  DNA_BASES = 'DNA_BASES',
  RNA_BASES = 'RNA_BASES',
  DNA_HELM = 'DNA_HELM',
  RNA_HELM = 'RNA_HELM',
  DNA_IDT = 'DNA_IDT',
  RNA_IDT = 'RNA_IDT',
  BASES = 'BASES',
  // Whether a DNA sequence is linear or circular.
  SEQUENCE_TOPOLOGY = 'SEQUENCE_TOPOLOGY',
  SMILES = 'SMILES',
  STAGE_RUN = 'STAGE_RUN',
  EXISTING_BIOENTITY = 'EXISTING_BIOENTITY',
  // The BioentitySelector column mimics a TagValue/Bioentity field, but doesn't require the cell to be a schema field
  BIOENTITY_SELECTOR = 'BIOENTITY_SELECTOR',
  BIOENTITY_NAME = 'BIOENTITY_NAME',
  ALIAS = 'ALIAS',

  // Inventory table columns
  NUMBER_OF_CONTAINERS = 'NUMBER_OF_CONTAINERS',
  CONTAINERS = 'CONTAINERS',
  PARENT_STORABLE = 'PARENT_STORABLE',
  POSITION = 'POSITION',
  QUANTITY = 'VOLUME',
  CONCENTRATION = 'CONCENTRATION',
  SAMPLE = 'SAMPLE',
  PLATE = 'PLATE',
  BOX = 'BOX',
  PARTIES = 'PARTIES',
  SAMPLE_RESTRICTION_STATUS = 'SAMPLE_RESTRICTION_STATUS',

  // Process columns
  PROCESS_TASK = 'PROCESS_TASK',
  PROCESS_TASK_ASSIGNEE = 'PROCESS_TASK_ASSIGNEE',
  PROCESS_TASK_SCHEDULED_ON_DATE = 'PROCESS_TASK_SCHEDULED_ON_DATE',
  PROCESS_TASK_STATUS = 'PROCESS_TASK_STATUS',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',

  // Mixture table columns
  MEASUREMENT_AMOUNT = 'MEASUREMENT_AMOUNT',
  INGREDIENT_CATALOG_ID = 'INGREDIENT_CATALOG_ID',
  // Complex polymer columns
  COMPLEX_POLYMER_COMPONENT = 'COMPLEX_POLYMER_COMPONENT',
  COMPLEX_POLYMER_CONNECTIONS = 'COMPLEX_POLYMER_CONNECTIONS',
  OLIGO_DUPLEX_OFFSET = 'OLIGO_DUPLEX_OFFSET',
  // TODO(BNCH-17431): Update string value to match variable name after migration on structured_table_column
  INGREDIENT_COMPONENT_LOT_TEXT = 'INGREDIENT_SOURCE',
  INGREDIENT_NOTES = 'INGREDIENT_NOTES',

  // Bulk assembly columns
  BULK_ASSEMBLY_FRAGMENT_POLYMER = 'BULK_ASSEMBLY_FRAGMENT_POLYMER',
  BULK_ASSEMBLY_BIN_SELECTOR = 'BULK_ASSEMBLY_BIN_SELECTOR',
  BULK_ASSEMBLY_FRAGMENT_ENDPOINT = 'BULK_ASSEMBLY_FRAGMENT_ENDPOINT',
  BULK_ASSEMBLY_LENGTH = 'BULK_ASSEMBLY_LENGTH',
  BULK_ASSEMBLY_CONSTRUCT_NAME = 'BULK_ASSEMBLY_CONSTRUCT_NAME',
  BULK_ASSEMBLY_VALIDATION_STATUS = 'BULK_ASSEMBLY_VALIDATION_STATUS',
  BULK_ASSEMBLY_PRIMER = 'BULK_ASSEMBLY_PRIMER',
  BULK_ASSEMBLY_PRIMER_BASES = 'BULK_ASSEMBLY_PRIMER_BASES',
  BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR = 'BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR',
  BULK_ASSEMBLY_PREFERRED_PRIMER = 'BULK_ASSEMBLY_PREFERRED_PRIMER',
  BULK_ASSEMBLY_RESTRICTION_ENZYME = 'BULK_ASSEMBLY_RESTRICTION_ENZYME',

  // Plate map columns
  ANONYMOUS_TAG_VALUE = 'ANONYMOUS_TAG_VALUE',
  EXPERIMENTAL_ROLE = 'EXPERIMENTAL_ROLE',
  FILL_PLATE_MEASUREMENT_AMOUNT = 'FILL_PLATE_MEASUREMENT_AMOUNT',
  FILL_PLATE_TRANSFER_SOURCE_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_CHIP',
  FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP',
  FILL_PLATE_TRANSFER_SOURCE_ICON = 'FILL_PLATE_TRANSFER_SOURCE_ICON',
  FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP',
  PLATE_MAP_MATERIAL_INPUT = 'PLATE_MAP_MATERIAL_INPUT',

  // Procedure columns
  PROCEDURE_PARAMETER_CONFIRMED_IN = 'PROCEDURE_PARAMETER_CONFIRMED_IN',
  PROCEDURE_PARAMETER_MIN_MAX = 'PROCEDURE_PARAMETER_MIN_MAX',
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',

  // Other columns
  // [Deprecated] Contains legacy hard-coded unit strings
  UNIT_SELECTOR = 'UNIT_SELECTOR',
  // [Preferred] Contains references to Unit Dictionary models
  UNIT_REFERENCE = 'UNIT_REFERENCE',
  DEDICATED_LOOKUP = 'DEDICATED_LOOKUP',
  CUSTOM = 'CUSTOM',
  STUDIES = 'STUDIES',
  ROW_DELEGATE = 'ROW_DELEGATE',

  // Additional columns that aren't persisted yet.  If these are ever
  // to be persisted, we should add them to the TableColumnType enum
  // on the backend.
  FULFILLABLE_STATUS = 'FULFILLABLE_STATUS',
  REQUEST_TASK_SELECTION = 'REQUEST_TASK_SELECTION',
  SAMPLE_GROUP_SAMPLE = 'SAMPLE_GROUP_SAMPLE',
  STAGE_RUN_SAMPLE = 'STAGE_RUN_SAMPLE',
  OPTION_SELECTOR = 'OPTION_SELECTOR',
  OPTION_SEARCH_SELECTOR = 'OPTION_SEARCH_SELECTOR',
  LOOKUP_CONFIG = 'LOOKUP_CONFIG',
  PLATE_LAYOUT_SELECTOR = 'PLATE_LAYOUT_SELECTOR',
  PROCESS_TASK_EXECUTION_ORIGIN = 'PROCESS_TASK_EXECUTION_ORIGIN',
  PROCEDURE = 'PROCEDURE',
  PROCEDURE_LIBRARY_PARAMETER_SELECTOR = 'PROCEDURE_LIBRARY_PARAMETER_SELECTOR',
  PROCEDURE_SCHEMA_FIELD_TYPE = 'PROCEDURE_SCHEMA_FIELD_TYPE',
  // If the tag schema field has been removed, then this column type will be used
  MISSING_FIELD = 'MISSING_FIELD',
  TEST_DEFINITION = 'TEST_DEFINITION',

  // Antibody columns
  ANTIBODY_DOMAIN = 'ANTIBODY_DOMAIN',
}

export const COLUMN_DEFINITION_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ALIAS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BATCH]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BIOENTITY_NAME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BOOLEAN]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BOX]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CONCENTRATION]),
    units: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CONTAINERS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DEDICATED_LOOKUP]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CUSTOM]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.EXISTING_BIOENTITY]),
    tagSchemaOrInterface: PropTypes.oneOfType([
      RegistryConstants.SCHEMA_SUMMARY_SHAPE,
      PropTypes.oneOf(['ALL']),
    ]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BIOENTITY_SELECTOR]),
    schemaFieldForFiltering: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FULFILLABLE_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.MISSING_FIELD]),
    structuredTableColumnName: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.NUMBER_OF_CONTAINERS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PARENT_STORABLE]),
    isForTransfer: PropTypes.bool.isRequired,
    // Schema of storables that will go in the parent storable
    storableSchema: STORABLE_SCHEMA_SHAPE,
    // Whether the parent storable is a Grid or a Location.
    // If null, both are allowed when not using inline location selector.
    // DEPRECATED - prefer parentStorableType for new structured tables.
    parentStorableIsGrid: PropTypes.bool,
    // Allow Grid, Location or both for parent storable in inline location selector
    parentStorableType: PropTypes.oneOf(Object.values(ParentStorableType)),
    fallbackRegistryId: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PARTIES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.POSITION]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.REQUEST_TASK_SELECTION]),
    requestTask: REQUEST_TASK_GRAPHQL_SHAPE.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE_GROUP_SAMPLE]),
    schemaField: SCHEMA_FIELD_SHAPE,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE_RESTRICTION_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STRING]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.TAG_VALUE]),
    schemaField: SCHEMA_FIELD_SHAPE,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STAGE_RUN]),
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.UNIT_SELECTOR]),
    acceptedUnits: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.OPTION_SELECTOR]),
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          node: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
          onUpdateKey: PropTypes.string,
        }),
      ])
    ),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.OPTION_SEARCH_SELECTOR]),
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        }),
      ])
    ),
    isMulti: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.MEASUREMENT_AMOUNT]),
    name: PropTypes.string,
    isComputed: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_CATALOG_ID]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_NOTES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.QUANTITY]),
    units: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ANONYMOUS_TAG_VALUE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.EXPERIMENTAL_ROLE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE_MAP_MATERIAL_INPUT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_LENGTH]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PRIMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PRIMER_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.LOOKUP_CONFIG]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE]),
    procedureTypes: PropTypes.arrayOf(PropTypes.string.isRequired),
    placeholder: PropTypes.string,
    shouldAllowMultiple: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_CONDITION_REPLICATES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE_LAYOUT_SELECTOR]),
    runSchema: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_ASSIGNEE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE]),
    context: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.TEST_DEFINITION]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_OUTPUT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_HELM]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_HELM]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_IDT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_IDT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SMILES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STUDIES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ROW_DELEGATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.UNIT_REFERENCE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SEQUENCE_TOPOLOGY]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ANTIBODY_DOMAIN]),
    domainType: PropTypes.oneOf(Object.values(AntibodyDomainType)),
    specificity: PropTypes.string,
  }),
]);

export interface TooltipIcon {
  icon: BenchlingIconName;
  tooltip: Optional<string>;
}

export interface ColumnConstraint<TColumnType = StructuredTableColumn> {
  type?: typeof SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD;
  columnDefinition?: ColumnDefinition | null;
  structuredTableColumn?: TColumnType;
  // Tag schema field used for some specialized cases when a render strategy is unspecified.
  tagSchemaFieldId?: Optional<number>;
  tagSchemaField?: SchemaField;

  // Used by plain tables to lock columns
  isProtected?: boolean;

  // Controls whether the column may be deleted/reinserted by the user.
  // Supercedes the legacy behavior for column insertion/deletion controled by the deprecated field
  // TableViewProps.legacyFirstEditableColumnIndex. When this field is set, that field will be ignored.
  isColumnUserConfigurable?: boolean;

  // Currently not persisted and used by DataTables for threading render information for columns
  tooltipIcons?: Array<TooltipIcon>;
  renderedLabel?: React.ReactNode;
  userAnnotationIndicator?: Optional<JSX.Element>;
  tooltip?: Optional<string>;
  canResetLabel?: Optional<boolean>;
  editLabelDisabled?: boolean;
  viewer?: boolean;
}

export const columnConstraint = PropTypes.shape({
  type: PropTypes.oneOf([SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD]),
  columnDefinition: COLUMN_DEFINITION_SHAPE,
  // Tag schema field used for some specialized cases when a render strategy is unspecified.
  tagSchemaFieldId: PropTypes.number,
  tagSchemaField: SCHEMA_FIELD_SHAPE,

  // Currently not persisted and used by DataTables for threading render information for columns
  tooltipIcons: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
    }).isRequired
  ),
  renderedLabel: PropTypes.node,
  userAnnotationIndicator: PropTypes.node,
  tooltip: PropTypes.string,
  canResetLabel: PropTypes.bool,
  viewer: PropTypes.bool,
  editLabelDisabled: PropTypes.bool,
});

// Can be threaded into TableOps.insertColumns(Left|Right) to insert columns with column fields set.
// Note that the key names match the exact keys in TableConstants.table, despite being
// only mapped to a single value.
export interface InsertableColumnInfo<TColumnType = StructuredTableColumn> {
  columnLabels: string;
  columnConstraints?: ColumnConstraint<TColumnType>;
  widths?: number;
  dataTestAttrs?: DataTestAttrs;
}

// Can be threaded into TableOps.insertColumns(Left|Right) to insert columns with column fields set.
// Note that the key names match the exact keys in export const PropTypes.table, despite being
// only mapped to a single value.
export const insertableColumnInfo = PropTypes.shape({
  widths: PropTypes.number,
  columnConstraints: columnConstraint,
  columnLabels: PropTypes.string,
});

// The max length for a warehouse name is 58 chars, but user created columns do not sync to the warehouse
// 70 is an arbitrary number based on the longest names in use before the limit was enforced.
// Agent keep in sync: MAX_COLUMN_NAME_LENGTH in benchling/notebook/plain_tables/validation.py
export const USER_CREATED_COLUMN_NAME_MAX_LENGTH = 70;

// Structured Table Columns that are created by the user after table insertion
// These columns are able to be deleted and renamed at any time
export const USER_CREATED_COLUMN_TYPES = [TableColumnType.CUSTOM, TableColumnType.DEDICATED_LOOKUP];
